import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaFacebook, 
  FaLinkedin, 
  FaInstagram,
  FaEnvelope,
  FaPhone,
  FaChevronRight,
  FaGlobe,
  FaLaptopCode,
  FaShieldAlt,
  FaServer,
  FaUsersCog
} from 'react-icons/fa';
import { 
  Divider, 
  Input, 
  Button, 
  Card, 
  CardBody, 
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem
} from "@heroui/react";

const Footer = () => {
  // Current year for copyright
  const year = new Date().getFullYear();

  // Newsletter state
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);

  const validateEmail = (email) => {
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    console.log('Walidacja email:', email, isValid);
    return isValid;
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setIsEmailValid(true);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Próba wysłania newslettera:', email);
    
    // Ważne: sprawdzamy validację
    if (!validateEmail(email)) {
      console.log('Email niepoprawny, pokazuję błąd');
      setIsEmailValid(false);
      return;
    }
    
    console.log('Email poprawny, wysyłam żądanie');
    setIsSubmitting(true);
    
    try {
      // Wywołanie API
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      console.log('Odpowiedź API:', data);
      
      if (response.ok) {
        // Sukces
        setSubscriptionSuccess(true);
        setEmail('');
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubscriptionSuccess(false);
        }, 5000);
      } else {
        // Błąd zwrócony przez API
        console.error('Newsletter subscription failed:', data.detail);
        setIsEmailValid(false);
      }
    } catch (error) {
      console.error('Error submitting newsletter:', error);
      setIsEmailValid(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal for privacy policy
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Language select state
  const [currentLanguage, setCurrentLanguage] = useState('pl');

  // Navigation links with categories
  const navigationLinks = {
    services: [
      { title: 'Infrastruktura IT', path: '/uslugi', icon: <FaServer className="text-xs" /> },
      { title: 'Bezpieczeństwo IT', path: '/uslugi', icon: <FaShieldAlt className="text-xs" /> },
      { title: 'Strony i Aplikacje', path: '/uslugi', icon: <FaLaptopCode className="text-xs" /> },
      { title: 'Wsparcie IT', path: '/uslugi', icon: <FaUsersCog className="text-xs" /> }
    ],
    company: [
      { title: 'O nas', path: '/o-nas' },
      { title: 'Portfolio', path: '/portfolio' },
      { title: 'Blog', path: '/blog' }
    ],
    legal: [
      { title: 'Polityka prywatności', path: '/polityka-prywatnosci', action: onOpen },
      { title: 'Warunki korzystania', path: '/warunki' },
      { title: 'Cookies', path: '/cookies' },
      { title: 'RODO', path: '/rodo' }
    ]
  };

  // Contact information
  const contactInfo = [
    { icon: <FaPhone className="mr-2" />, text: '+48 690 481 963', link: 'tel:+48690481963' },
    { icon: <FaEnvelope className="mr-2" />, text: 'kontakt@itfe.pro', link: 'mailto:kontakt@itfe.pro' }
    //{ icon: <FaMapMarkerAlt className="mr-2" />, text: 'ul. Przykładowa 123, 00-001 Warszawa', link: 'https://maps.google.com' },
    //{ icon: <FaClock className="mr-2" />, text: 'Pon-Pt: 9:00 - 17:00', link: null }
  ];

  // Animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 50, damping: 20 }
    }
  };

  return (
    <footer className="bg-background pt-16 pb-8 border-t border-divider relative overflow-hidden">
      {/* Top gradient border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-30" />
      
      {/* Background elements */}
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full filter blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/5 rounded-full filter blur-3xl" />
      
      {/* Top section with cards */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 mb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Quick contact card */}
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 shadow-md">
            <CardBody>
              <div className="flex items-center gap-4 mb-4">
                <div className="rounded-full bg-primary/20 p-3">
                  <FaPhone className="text-primary text-lg" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Szybki kontakt</h3>
                  <p className="text-sm text-foreground-muted">Potrzebujesz pomocy? Jesteśmy dostępni!</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 mb-4">
                <div className="flex gap-2 items-center">
                  <FaPhone className="text-primary text-sm" />
                  <span className="text-foreground font-medium">+48 690 481 963</span>
                </div>
                <div className="flex gap-2 items-center">
                  <FaEnvelope className="text-primary text-sm" />
                  <span className="text-foreground font-medium">kontakt@itfe.pro</span>
                </div>
              </div>
              <Button 
                as="a"
                href="/kontakt"
                color="primary"
                fullWidth
                className="mt-2"
                variant="flat"
              >
                Skontaktuj się z nami
              </Button>
            </CardBody>
          </Card>
          
          {/* Newsletter card */}
          <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5 border border-secondary/20 shadow-md">
            <CardBody>
              <div className="flex items-center gap-4 mb-4">
                <div className="rounded-full bg-secondary/20 p-3">
                  <FaEnvelope className="text-secondary text-lg" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Newsletter</h3>
                  <p className="text-sm text-foreground-muted">Bądź na bieżąco z nowościami</p>
                </div>
              </div>
              <form onSubmit={handleNewsletterSubmit}>
                <div className="flex flex-col gap-3">
                  <Input 
                    type="email" 
                    placeholder="Twój adres email" 
                    value={email}
                    onChange={handleEmailChange}
                    isInvalid={!isEmailValid}
                    errorMessage={!isEmailValid ? "Podaj poprawny adres email" : ""}
                    color={!isEmailValid ? "danger" : "secondary"}
                    variant="bordered"
                  />
                  {subscriptionSuccess ? (
                    <div className="flex items-center gap-2 text-success">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm">Zapisano do newslettera!</span>
                    </div>
                  ) : (
                    <Button
                      type="submit"
                      color="secondary"
                      fullWidth
                      isLoading={isSubmitting}
                    >
                      Zapisz się
                    </Button>
                  )}
                </div>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
      
      {/* Main footer content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Logo and about */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/" className="flex items-center mb-4">
                <div className="bg-gradient-to-r from-primary to-secondary text-white text-2xl font-bold px-4 py-2 rounded-md">
                  ITFE
                </div>
              </Link>
              <p className="text-foreground-muted mb-6 max-w-md">
                IT For Everyone to firma tworzona z pasją do dostarczania prostych i skutecznych rozwiązań IT dla biznesu. 
                Wierzymy, że technologia powinna być dostępna i zrozumiała dla wszystkich.
              </p>
              
              {/* Social media */}
              <div className="flex gap-2 mb-6">
                <motion.a
                  href="#"
                  className="w-8 h-8 rounded-full bg-default-100 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors duration-300"
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaFacebook />
                </motion.a>
                <motion.a
                  href="#"
                  className="w-8 h-8 rounded-full bg-default-100 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors duration-300"
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaLinkedin />
                </motion.a>
                <motion.a
                  href="#"
                  className="w-8 h-8 rounded-full bg-default-100 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors duration-300"
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaInstagram />
                </motion.a>
              </div>
            </motion.div>
          </div>

          {/* Navigation links */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-4">
              Usługi
            </h3>
            <motion.ul className="space-y-3">
              {navigationLinks.services.map((link, index) => (
                <motion.li key={index} variants={itemVariants}>
                  <Link 
                    to={link.path}
                    className="text-foreground-muted hover:text-primary transition-colors flex items-center"
                  >
                    {link.icon && <span className="mr-2">{link.icon}</span>}
                    <span>{link.title}</span>
                  </Link>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-4">
              Firma
            </h3>
            <motion.ul className="space-y-3">
              {navigationLinks.company.map((link, index) => (
                <motion.li key={index} variants={itemVariants}>
                  <Link 
                    to={link.path}
                    className="text-foreground-muted hover:text-primary transition-colors flex items-center"
                  >
                    <FaChevronRight className="h-3 w-3 mr-2" />
                    <span>{link.title}</span>
                  </Link>
                </motion.li>
              ))}
              <motion.li variants={itemVariants}>
                <Link 
                  to="/kontakt"
                  className="text-foreground-muted hover:text-primary transition-colors flex items-center"
                >
                  <FaChevronRight className="h-3 w-3 mr-2" />
                  <span>Kontakt</span>
                </Link>
              </motion.li>
              {navigationLinks.legal.map((link, index) => (
                <motion.li key={index} variants={itemVariants} className="text-xs">
                  {link.action ? (
                    <button 
                      onClick={link.action} 
                      className="text-foreground-muted hover:text-primary transition-colors text-left"
                    >
                      {link.title}
                    </button>
                  ) : (
                    <Link 
                      to={link.path}
                      className="text-foreground-muted hover:text-primary transition-colors"
                    >
                      {link.title}
                    </Link>
                  )}
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-4">
              Kontakt
            </h3>
            <motion.ul className="space-y-3">
              {contactInfo.map((item, index) => (
                <motion.li key={index} variants={itemVariants}>
                  {item.link ? (
                    <a 
                      href={item.link}
                      className="text-foreground-muted hover:text-primary transition-colors flex items-center"
                      target={item.link.startsWith('https') ? '_blank' : undefined}
                      rel={item.link.startsWith('https') ? 'noopener noreferrer' : undefined}
                    >
                      {item.icon}
                      <span>{item.text}</span>
                    </a>
                  ) : (
                    <div className="text-foreground-muted flex items-center">
                      {item.icon}
                      <span>{item.text}</span>
                    </div>
                  )}
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>

        {/* Bottom section */}
        <Divider className="my-6 opacity-50" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-foreground-muted text-sm flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-2">
            <span>&copy; {year} IT For Everyone. Wszelkie prawa zastrzeżone.</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Dropdown>
              <DropdownTrigger>
                <Button 
                  variant="light" 
                  size="sm"
                  startContent={<FaGlobe className="text-primary" />}
                  className="text-sm"
                >
                  {currentLanguage === 'pl' ? 'Polski' : 'English'}
                </Button>
              </DropdownTrigger>
              <DropdownMenu 
                aria-label="Wybór języka"
                selectedKeys={[currentLanguage]}
                onSelectionChange={(keys) => setCurrentLanguage(Array.from(keys)[0])}
                selectionMode="single"
              >
                <DropdownItem key="pl">Polski</DropdownItem>
                {/* <DropdownItem key="en">English</DropdownItem>*/}
              </DropdownMenu>
            </Dropdown>
            
            <Button 
              as="a"
              href="#"
              size="sm"
              variant="light"
              className="text-sm"
            >
              Mapa strony
            </Button>
          </div>
        </div>
      </div>
      
      {/* Privacy Policy Modal */}
      <Modal 
        isOpen={isOpen} 
        onClose={onClose}
        scrollBehavior="inside"
        size="2xl"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Polityka Prywatności
              </ModalHeader>
              <ModalBody>
                <h3 className="font-semibold mb-2">1. Informacje ogólne</h3>
                <p className="text-foreground-muted mb-4 text-sm">
                  Niniejsza polityka prywatności określa zasady przetwarzania i ochrony danych osobowych przekazanych przez Użytkowników w związku z korzystaniem z usług oferowanych przez stronę internetową.
                </p>
                
                <h3 className="font-semibold mb-2">2. Administrator danych</h3>
                <p className="text-foreground-muted mb-4 text-sm">
                  Administratorem danych osobowych jest IT For Everyone z siedzibą w Warszawie, ul. Przykładowa 123, 00-001 Warszawa.
                </p>

                <h3 className="font-semibold mb-2">3. Cel i zakres zbierania danych</h3>
                <p className="text-foreground-muted mb-4 text-sm">
                  Dane osobowe Użytkowników są zbierane w celu:
                </p>
                <ul className="list-disc ml-5 mb-4 text-sm space-y-1 text-foreground-muted">
                  <li>Realizacji usług świadczonych przez Administratora</li>
                  <li>Dostosowania zawartości strony internetowej do preferencji Użytkownika</li>
                  <li>Prowadzenia działań marketingowych</li>
                  <li>Wysyłania informacji handlowych (przy wyraźnej zgodzie Użytkownika)</li>
                </ul>

                <h3 className="font-semibold mb-2">4. Prawo dostępu</h3>
                <p className="text-foreground-muted mb-4 text-sm">
                  Użytkownik ma prawo dostępu do treści swoich danych osobowych oraz prawo ich sprostowania, usunięcia, ograniczenia przetwarzania, prawo do przenoszenia danych, prawo wniesienia sprzeciwu, prawo do cofnięcia zgody w dowolnym momencie.
                </p>

                <h3 className="font-semibold mb-2">5. Kontakt</h3>
                <p className="text-foreground-muted text-sm">
                  We wszelkich sprawach związanych z przetwarzaniem danych osobowych, w szczególności w celu realizacji uprawnień, o których mowa powyżej, Użytkownik może kontaktować się z Administratorem pod adresem e-mail: privacy@itfe.pro lub pisemnie na adres siedziby Administratora.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onPress={onClose}>
                  Zamknij
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </footer>
  );
};

export default Footer;
